import {useGtm} from '@gtm-support/vue-gtm'
import {useEventItemConverter} from '@/composables/useEventItemConverter'
import type {EventItemInput} from '@/types/event-item-converter'

type ItemType = 'product' | 'design'

export const useCcrsTracking = () => {
  // Helper function to track events
  const trackEvent = (eventName: string, props?: Record<string, any>) => {
    const options: EventItemInput = {
      event: eventName,
      add_personalization: true,
      ...(props && {props})
    }

    const ga4payload = useEventItemConverter().convert(options)
    useGtm()?.trackEvent(ga4payload)
  }

  const trackSearchEvent = (payload: Record<string, any>) => {
    trackEvent('ccrs_searchresult', payload)
  }

  const getItemTypeProps = (item: Record<string, any>): Record<string, any> => {
    const itemTypeProps: Record<ItemType, () => Record<string, any>> = {
      product: () => ({
        vendorName: item.vendorNameClean || item.vendorName,
        itemNumber: item.itemNumberClean || item.itemNumber,
        itemInternalId: item.id ?? '',
        title: item.titleClean || item.title || '',
        description: item.description || '',
        competitorId: item.competitorId ?? '',
        material: item.material || ''
      }),
      design: () => ({
        title: item.titleClean || item.title || '',
        description: item.description || '',
        category: item.categoryClean || item.category || '',
        internalDesignId: item.id ?? '',
        competitorId: item.competitorId ?? '',
        vendorName: item.vendorNameClean || item.vendorName || ''
      })
    }

    const type = item.type as string
    return type in itemTypeProps ? itemTypeProps[type as ItemType]() : {}
  }

  const trackResultClick = (item: Record<string, any>, searchTerm: string) => {
    trackEvent('ccrs_result_click', {
      searchTerm,
      resultType: item.type,
      resultId: item.id,
      ...getItemTypeProps(item)
    })
  }

  const trackVendorSelection = (vendorName: string | null, vendorId: number | null, searchTerm: string) => {
    trackEvent('ccrs_vendor', {
      vendorName,
      vendorId,
      searchTerm
    })
  }

  const trackShowContactDetails = (props: Record<string, string>) => {
    trackEvent('ccrs_showcontactdetails', props)
  }

  // Simple button events without props
  const trackResetToSearch = () => trackEvent('ccrs_resettosearch')
  const trackApplyFilter = (props: Record<string, string>) => trackEvent('ccrs_applyfilter', props)
  const trackAddToCart = (props: Record<string, string>) => trackEvent('ccrs_addtocart', props)
  const trackContactSend = () => trackEvent('ccrs_contact_send')
  const trackCopyMail = () => trackEvent('ccrs_copymail')
  const trackClickMail = (props: Record<string, string>) => trackEvent('ccrs_clickmail', props)

  return {
    trackSearchEvent,
    trackResultClick,
    trackVendorSelection,
    trackResetToSearch,
    trackApplyFilter,
    trackAddToCart,
    trackShowContactDetails,
    trackContactSend,
    trackCopyMail,
    trackClickMail
  }
}
